import React, { useState, useEffect, useRef } from "react";
import { useStateValue } from "../../StateProvider";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";
import { account, tablesDB, DB_ID, PRODUCTS_TABLE } from "../../appwrite";
import logo from "../Assets/images/logo.png";
import {
  Search,
  ShoppingCart,
  X,
  Home,
  Store,
  HelpCircle,
  User,
  LogOut,
} from "lucide-react";
import ContactForm from "./ContactForm";
import { Query } from "appwrite";

function slugify(text) {
  if (!text) return "";
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
}

export default function Navbar() {
  const [{ cart, user }, dispatch] = useStateValue();
  const navigate = useNavigate();
  const location = useLocation();

  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [products, setProducts] = useState([]);
  const [isContactFormVisible, setContactFormVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileQuery, setMobileQuery] = useState("");
  const [showMobileResults, setShowMobileResults] = useState(false);
  const [catValue, setCatValue] = useState("");
  const catRef = useRef(null);
  const searchRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef(null);

  const VALID_CATS = [
    "reup-vault",
    "value-packs",
    "fresh-farm-produce",
    "grains-and-staples",
    "meat-and-livestock",
    "dairy-and-eggs",
    "beverages",
    "snacks-and-confectionery",
    "canned-and-packaged-foods",
    "frozen-foods",
    "condiments-and-sauces",
  ];

  // Sync dropdown to current URL
  useEffect(() => {
    const match = location.pathname.match(/^\/shop\/(.+)/);
    const slug = match ? match[1] : "";
    setCatValue(VALID_CATS.includes(slug) ? slug : "");
  }, [location.pathname]);

  // Auto-resize select to fit selected text
  useEffect(() => {
    const sel = catRef.current;
    if (!sel) return;
    const text = sel.options[sel.selectedIndex]?.text || "All";
    const span = document.createElement("span");
    span.style.cssText =
      "position:absolute;top:-9999px;font-size:12px;font-weight:600;white-space:nowrap;padding:0 4px;";
    span.textContent = text;
    document.body.appendChild(span);
    sel.style.width = span.offsetWidth + 32 + "px";
    document.body.removeChild(span);
  }, [catValue]);

  const filteredItems = products.filter((p) =>
    (p.productName || "").toLowerCase().includes(query.toLowerCase()),
  );

  const filteredMobile = products.filter((p) =>
    (p.productName || "").toLowerCase().includes(mobileQuery.toLowerCase()),
  );

  const firstName = user
    ? (user.name || user.displayName || "").split(" ")[0] || "You"
    : null;

  const totalItems = cart.reduce((t, item) => t + item.quantity, 0);

  const handleSignOut = async () => {
    try {
      await account.deleteSession("current");
      dispatch({ type: "SET_USER", user: null });
    } catch {}
    navigate("/login");
  };

  useEffect(() => {
    tablesDB
      .listRows({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        queries: [Query.limit(500)],
      })
      .then((res) => {
        setProducts((res.rows || []).map((row) => ({ $id: row.$id, ...row })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      menuOpen || isContactFormVisible ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, isContactFormVisible]);

  const openMobileSearch = () => {
    setMobileSearchOpen(true);
    setTimeout(() => mobileSearchInputRef.current?.focus(), 60);
  };

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    setMobileQuery("");
    setShowMobileResults(false);
  };

  const goToProduct = (product) => {
    const slug = slugify(product.productName);
    setShowResults(false);
    setShowMobileResults(false);
    setMenuOpen(false);
    setMobileSearchOpen(false);
    setQuery("");
    setMobileQuery("");
    navigate(`/product/${slug}`);
  };

  const goToSearch = (q) => {
    const trimmed = (q || "").trim();
    if (!trimmed) return;
    setShowResults(false);
    setShowMobileResults(false);
    setMobileSearchOpen(false);
    setMenuOpen(false);
    setQuery("");
    setMobileQuery("");
    navigate(`/shop/all?search=${encodeURIComponent(trimmed)}`);
  };

  return (
    <>
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <div
          className={`nav__inner${mobileSearchOpen ? " nav__inner--searching" : ""}`}
        >
          {/* Logo */}
          <a href="/" className="nav__logo">
            <img src={logo} alt="TrendBay" />
          </a>

          {/* Search */}
          <div className="nav__search" ref={searchRef}>
            <select
              ref={catRef}
              className="nav__search-cat"
              value={catValue}
              onChange={(e) => {
                setCatValue(e.target.value);
                navigate(e.target.value ? `/shop/${e.target.value}` : "/shop");
              }}
            >
              <option value="">All</option>
              <option value="reup-vault">ReUp Vault</option>
              <option value="value-packs">Value Packs</option>
              <option value="fresh-farm-produce">Fresh Farm Produce</option>
              <option value="grains-and-staples">Grains &amp; Staples</option>
              <option value="meat-and-livestock">Meat &amp; Livestock</option>
              <option value="dairy-and-eggs">Dairy &amp; Eggs</option>
              <option value="beverages">Beverages</option>
              <option value="snacks-and-confectionery">Snacks</option>
              <option value="canned-and-packaged-foods">Canned Foods</option>
              <option value="frozen-foods">Frozen Foods</option>
              <option value="condiments-and-sauces">Condiments</option>
            </select>
            <input
              className="nav__search-input"
              type="text"
              placeholder="Search TrendBay…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowResults(e.target.value.length > 0);
              }}
              onKeyDown={(e) => e.key === "Enter" && goToSearch(query)}
            />
            <button
              className="nav__search-btn"
              aria-label="Search"
              onClick={() => goToSearch(query)}
            >
              <Search size={16} />
            </button>

            {showResults && (
              <div className="nav__search-results">
                {filteredItems.length === 0 ? (
                  <div className="nav__result-empty">No results found</div>
                ) : (
                  filteredItems.slice(0, 8).map((product) => (
                    <div
                      key={product.$id}
                      className="nav__result-item"
                      onClick={() => goToProduct(product)}
                    >
                      <img
                        src={product.productImage}
                        alt={product.productName}
                      />
                      <p>{product.productName}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Desktop nav links */}
          <div className="nav__links">
            <div className="nav__dual">
              <Link to="/shop" className="nav__dual-top">
                Shop by Category
              </Link>
              <Link to="/shop/all" className="nav__dual-bot">
                All Products
              </Link>
            </div>
            <Link to="/frequently-asked-questions" className="nav__stack">
              <span className="nav__stack-top">Need Help?</span>
              <span className="nav__stack-bot">F.A.Q</span>
            </Link>
            <Link to="/orders" className="nav__stack">
              <span className="nav__stack-top">Track</span>
              <span className="nav__stack-bot">Orders</span>
            </Link>
            <Link to="/account" className="nav__stack">
              <span className="nav__stack-top">My</span>
              <span className="nav__stack-bot">Account</span>
            </Link>
            {user ? (
              <button className="nav__stack" onClick={handleSignOut}>
                <span className="nav__stack-top">Hello, {firstName}</span>
                <span className="nav__stack-bot">Sign Out</span>
              </button>
            ) : (
              <>
                <Link to="/login" className="nav__stack">
                  <span className="nav__stack-top">Hello, Guest</span>
                  <span className="nav__stack-bot">Sign In</span>
                </Link>
                <Link to="/signup" className="nav__signup-btn">
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Cart */}
          <Link to="/cart" className="nav__cart">
            <ShoppingCart size={24} />
            {totalItems > 0 && (
              <span className="nav__cart-badge">{totalItems}</span>
            )}
          </Link>

          {/* Hamburger */}
          <button
            className={`nav__hamburger${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            <span />
            <span />
            <span />
          </button>

          {/* Mobile search icon - visible only on mobile via CSS */}
          <button
            className="nav__mob-search-btn"
            onClick={openMobileSearch}
            aria-label="Search"
          >
            <Search size={18} />
          </button>

          {/* Mobile inline search bar - expands over the nav on mobile */}
          <div
            className={`nav__mob-search-bar${mobileSearchOpen ? " open" : ""}`}
          >
            <Search size={16} color="rgba(255,255,255,0.4)" />
            <input
              ref={mobileSearchInputRef}
              type="search"
              className="nav__mob-search-input"
              placeholder="Search TrendBay…"
              value={mobileQuery}
              onChange={(e) => {
                setMobileQuery(e.target.value);
                setShowMobileResults(e.target.value.length > 0);
              }}
              onKeyDown={(e) => e.key === "Enter" && goToSearch(mobileQuery)}
            />
            <button
              className="nav__mob-search-close"
              onClick={closeMobileSearch}
              aria-label="Close search"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Mobile search results - fixed below nav */}
        {showMobileResults && mobileSearchOpen && (
          <div className="nav__mob-results">
            {filteredMobile.length === 0 ? (
              <div className="nav__result-empty">No results found</div>
            ) : (
              filteredMobile.slice(0, 6).map((product) => (
                <div
                  key={product.$id}
                  className="nav__result-item"
                  onClick={() => goToProduct(product)}
                >
                  <img src={product.productImage} alt={product.productName} />
                  <p>{product.productName}</p>
                </div>
              ))
            )}
          </div>
        )}
      </nav>

      {/* Mobile overlay */}
      <div className={`nav__overlay${menuOpen ? " open" : ""}`}>
        <div className="nav__overlay-top">
          <a href="/" className="nav__logo" onClick={() => setMenuOpen(false)}>
            <img src={logo} alt="TrendBay" />
          </a>
          <button
            className="nav__overlay-close"
            onClick={() => setMenuOpen(false)}
          >
            <X size={16} />
          </button>
        </div>

        <div className="nav__overlay-search">
          <Search size={16} color="rgba(255,255,255,0.4)" />
          <input
            type="text"
            placeholder="Search TrendBay…"
            value={mobileQuery}
            onChange={(e) => {
              setMobileQuery(e.target.value);
              setShowMobileResults(e.target.value.length > 0);
            }}
            onKeyDown={(e) => e.key === "Enter" && goToSearch(mobileQuery)}
          />
          {showMobileResults && (
            <div className="nav__overlay-results nav__search-results">
              {filteredMobile.length === 0 ? (
                <div className="nav__result-empty">No results found</div>
              ) : (
                filteredMobile.slice(0, 6).map((product) => (
                  <div
                    key={product.$id}
                    className="nav__result-item"
                    onClick={() => goToProduct(product)}
                  >
                    <img src={product.productImage} alt={product.productName} />
                    <p>{product.productName}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="nav__overlay-links">
          <Link
            to="/"
            className="nav__overlay-link"
            onClick={() => setMenuOpen(false)}
          >
            <Home size={18} /> Home
          </Link>
          <Link
            to="/shop"
            className="nav__overlay-link"
            onClick={() => setMenuOpen(false)}
          >
            <Store size={18} /> Shop by Category
          </Link>
          <Link
            to="/shop/all"
            className="nav__overlay-link"
            onClick={() => setMenuOpen(false)}
          >
            <Store size={18} /> All Products
          </Link>
          <Link
            to="/cart"
            className="nav__overlay-link"
            onClick={() => setMenuOpen(false)}
          >
            <ShoppingCart size={18} /> Cart{" "}
            {totalItems > 0 && `(${totalItems})`}
          </Link>
          <Link
            to="/orders"
            className="nav__overlay-link"
            onClick={() => setMenuOpen(false)}
          >
            <User size={18} /> Orders
          </Link>
          <Link
            to="/frequently-asked-questions"
            className="nav__overlay-link"
            onClick={() => setMenuOpen(false)}
          >
            <HelpCircle size={18} /> F.A.Q
          </Link>
          <div className="nav__overlay-divider" />
          {user ? (
            <button
              className="nav__overlay-link"
              onClick={() => {
                handleSignOut();
                setMenuOpen(false);
              }}
            >
              <LogOut size={18} /> Logout ({firstName})
            </button>
          ) : (
            <div className="nav__overlay-cta">
              <Link
                to="/login"
                className="btn-secondary"
                onClick={() => setMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="btn-primary"
                onClick={() => setMenuOpen(false)}
              >
                Create Account
              </Link>
            </div>
          )}
        </div>
      </div>

      {isContactFormVisible && (
        <div className="contact-overlay">
          <div
            className="contact-form-container"
            onClick={(e) => e.stopPropagation()}
          >
            <ContactForm
              isVisible={isContactFormVisible}
              onClose={() => setContactFormVisible(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
